import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageSquare, RefreshCw, X, Send, Loader2, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { useAvailableVoices, useSpeak } from './VoiceQuestion';
import { configureEnglishRecognition, getSpeechRecognitionConstructor, getSttAvailability, getSttErrorMessage } from '../utils/speechRecognition';
import { getRoleplayVoiceForClientGender } from '../utils/roleplayVoice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTIES = [
  { key: 'mild', label: 'Mild', color: 'emerald', hint: 'Cooperative, reflective' },
  { key: 'standard', label: 'Standard', color: 'indigo', hint: 'Persona-baseline resistance' },
  { key: 'hostile', label: 'Hostile', color: 'rose', hint: 'Skeptical, guarded, pushes back' },
];

const DIFF_STYLES = {
  emerald: {
    active: 'bg-emerald-600 text-white border-emerald-700',
    inactive: 'text-emerald-800 border-emerald-200 hover:bg-emerald-50',
  },
  indigo: {
    active: 'bg-indigo-600 text-white border-indigo-700',
    inactive: 'text-indigo-800 border-indigo-200 hover:bg-indigo-50',
  },
  rose: {
    active: 'bg-rose-600 text-white border-rose-700',
    inactive: 'text-rose-800 border-rose-200 hover:bg-rose-50',
  },
};

const SAFE_ROLEPLAY_ERRORS = {
  ROLEPLAY_RATE_LIMITED: 'Roleplay is temporarily busy. Please try again shortly.',
  ROLEPLAY_TIMEOUT: 'The roleplay response took too long. Please try again.',
  ROLEPLAY_UNAVAILABLE: 'Roleplay is temporarily unavailable. Please try again shortly.',
  ROLEPLAY_CONFIGURATION_ERROR: 'Roleplay is temporarily unavailable. Please contact the administrator.',
  ROLEPLAY_INVALID_RESPONSE: 'The roleplay response could not be completed. Please try again.',
  ROLEPLAY_INTERNAL_ERROR: 'Something went wrong while generating the roleplay response. Please try again.',
};
const GENERIC_ROLEPLAY_ERROR = SAFE_ROLEPLAY_ERRORS.ROLEPLAY_INTERNAL_ERROR;

/**
 * NexusRoleplaySimulator
 * Globally-mounted modal opened via `window.dispatchEvent(new CustomEvent('nexus-roleplay-open', { detail: { sessionId } }))`.
 * Includes mic (Web Speech STT), TTS auto-reply, and a difficulty selector.
 */
export default function NexusRoleplaySimulator() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const [difficulty, setDifficulty] = useState('standard');
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [sttError, setSttError] = useState('');
  const [clientGender, setClientGender] = useState(null);
  const [roleplayVoiceURI, setRoleplayVoiceURI] = useState('');
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const sendingRef = useRef(false);
  const sttSeedRef = useRef('');           // text that was in the input BEFORE mic started
  const userStoppedRef = useRef(false);
  const availableVoices = useAvailableVoices();
  const { play: speak, stop: stopSpeaking, supported: ttsSupported } = useSpeak(roleplayVoiceURI || null, 1.0);

  const sttAvailability = getSttAvailability();
  const sttSupported = sttAvailability.available;

  useEffect(() => {
    const onOpen = async (e) => {
      const sid = e?.detail?.sessionId || null;
      if (!sid) return;
      setSessionId(sid);
      setMessages([]);
      setInput('');
      setRetrySeconds(0);
      setSttError('');
      sendingRef.current = false;
      setDifficulty('standard');
      setClientGender(null);
      setRoleplayVoiceURI('');
      setOpen(true);
      try {
        const token = sessionStorage.getItem('token');
        const response = await axios.get(`${API}/sessions/${sid}/phase1`, { headers: { Authorization: `Bearer ${token}` } });
        setClientGender(response.data?.gender ?? null);
      } catch (_) {
        setClientGender(null);
      }
    };
    window.addEventListener('nexus-roleplay-open', onOpen);
    return () => window.removeEventListener('nexus-roleplay-open', onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const matched = getRoleplayVoiceForClientGender(clientGender, availableVoices);
    setRoleplayVoiceURI(matched?.voiceURI || '');
  }, [open, clientGender, availableVoices]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Stop mic + TTS whenever the modal closes
  useEffect(() => {
    if (!open) {
      stopListening();
      try { stopSpeaking(); } catch (_) { /* ignore */ }
    }
  }, [open]);

  useEffect(() => {
    if (retrySeconds <= 0) return undefined;
    const timer = window.setInterval(() => setRetrySeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [retrySeconds > 0]);

  const close = () => setOpen(false);
  const reset = () => {
    setMessages([]);
    setInput('');
    setRetrySeconds(0);
    stopListening();
    try { stopSpeaking(); } catch (_) { /* ignore */ }
  };

  const startListening = () => {
    if (!sttSupported) { setSttError(sttAvailability.message); return; }
    if (isListening || recognitionRef.current) return;
    const SR = getSpeechRecognitionConstructor();
    try {
      const rec = new SR();
      configureEnglishRecognition(rec);
      // Snapshot the current typed input so re-processing all results doesn't
      // wipe or duplicate what the therapist already typed before hitting mic.
      sttSeedRef.current = input || '';
      userStoppedRef.current = false;
      rec.onstart = () => { setIsListening(true); setSttError(''); };
      rec.onspeechend = () => {
        // Auto-stop the mic the moment the therapist stops speaking, so Chrome
        // doesn't re-hear its own TTS echo (which fires the SAME final chunk
        // again) or ambient noise as a second utterance.
        if (!userStoppedRef.current) {
          userStoppedRef.current = true;
          try { rec.stop(); } catch (_) { /* ignore */ }
        }
      };
      rec.onresult = (evt) => {
        // Rebuild the transcript idempotently from ALL results each event and
        // dedupe consecutive identical final chunks (Chrome commonly emits the
        // same final phrase twice, producing "hello hello"). This mirrors the
        // logic in VoiceQuestion.jsx used across Phases 1-4.
        const finals = [];
        let interim = '';
        for (let i = 0; i < evt.results.length; i++) {
          const chunk = (evt.results[i][0]?.transcript || '').trim();
          if (!chunk) continue;
          if (evt.results[i].isFinal) {
            const last = finals[finals.length - 1];
            if (!last || last.toLowerCase() !== chunk.toLowerCase()) {
              finals.push(chunk);
            }
          } else {
            interim += (interim ? ' ' : '') + chunk;
          }
        }
        const dictated = [finals.join(' '), interim].filter(Boolean).join(' ').trim();
        const seed = sttSeedRef.current.trim();
        const merged = [seed, dictated].filter(Boolean).join(' ');
        setInput(merged);
      };
      rec.onerror = (event) => { setSttError(getSttErrorMessage(event?.error)); setIsListening(false); recognitionRef.current = null; };
      rec.onend = () => { setIsListening(false); recognitionRef.current = null; };
      recognitionRef.current = rec;
      rec.start();
    } catch (_) {
      recognitionRef.current = null;
      setSttError(getSttErrorMessage());
      setIsListening(false);
    }
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch (_) { /* ignore */ }
    recognitionRef.current = null;
    setIsListening(false);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sendingRef.current || retrySeconds > 0 || !sessionId) return;
    sendingRef.current = true;
    stopListening();
    try { stopSpeaking(); } catch (_) { /* ignore */ }
    const history = [...messages];
    setMessages([...history, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.post(
        `${API}/sessions/${sessionId}/phase5/nexus/roleplay`,
        { message: text, history, difficulty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const reply = res.data?.reply || '(no reply)';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
      if (ttsEnabled && ttsSupported && reply) {
        try { speak(reply); } catch (_) { /* ignore */ }
      }
    } catch (err) {
      const error = err.response?.data?.error;
      const code = typeof error?.code === 'string' ? error.code : '';
      const safeMessage = SAFE_ROLEPLAY_ERRORS[code] || GENERIC_ROLEPLAY_ERROR;
      const retryAfter = Number.isInteger(error?.retry_after_seconds)
        ? Math.min(3600, Math.max(0, error.retry_after_seconds))
        : 0;
      setRetrySeconds(retryAfter);
      setMessages((prev) => [...prev, { role: 'status', content: safeMessage }]);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      data-testid="nexus-roleplay-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col"
        data-testid="nexus-roleplay-dialog"
        role="dialog"
        aria-label="Client Roleplay Simulator"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-emerald-50">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare className="w-5 h-5 text-emerald-700 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-emerald-900 truncate" data-testid="nexus-roleplay-title">
                Client Roleplay Simulator
              </h3>
              <p className="text-xs text-emerald-800/80 truncate">
                In-character. Practice reflections, reframes, and objection handling.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setTtsEnabled((v) => !v)}
              className={`text-xs rounded p-1.5 inline-flex items-center ${ttsEnabled ? 'text-emerald-800 hover:bg-emerald-100' : 'text-gray-400 hover:bg-gray-100'}`}
              data-testid="btn-roleplay-tts-toggle"
              title={ttsEnabled ? 'Voice playback ON — click to mute' : 'Voice playback OFF — click to enable'}
              disabled={!ttsSupported}
            >
              {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={sending || messages.length === 0}
              className="text-xs text-emerald-700 hover:bg-emerald-100 rounded px-2 py-1 inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="btn-roleplay-reset"
              title="Reset conversation"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset
            </button>
            <button
              type="button"
              onClick={close}
              className="text-emerald-700 hover:bg-emerald-100 rounded p-1.5 inline-flex items-center"
              data-testid="btn-roleplay-close"
              title="Close simulator"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Difficulty selector */}
        <div className="flex items-center gap-2 px-5 py-2 border-b bg-gray-50 flex-wrap" data-testid="nexus-roleplay-difficulty">
          <span className="text-xs uppercase tracking-wider font-semibold text-gray-600">Difficulty:</span>
          {DIFFICULTIES.map((d) => {
            const styles = DIFF_STYLES[d.color];
            const active = difficulty === d.key;
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => setDifficulty(d.key)}
                className={`text-xs font-medium border rounded-full px-3 py-1 transition-colors ${active ? styles.active : `bg-white ${styles.inactive}`}`}
                data-testid={`btn-difficulty-${d.key}`}
                title={d.hint}
              >
                {d.label}
              </button>
            );
          })}
          <span className="text-[11px] italic text-gray-500 ml-1 hidden sm:inline">
            {DIFFICULTIES.find((d) => d.key === difficulty)?.hint}
          </span>
          <label className="text-xs text-gray-600 ml-auto" htmlFor="roleplay-voice">Client voice:</label>
          <select id="roleplay-voice" data-testid="roleplay-voice-select" value={roleplayVoiceURI} onChange={(e) => setRoleplayVoiceURI(e.target.value)} className="text-xs border rounded px-2 py-1 bg-white max-w-[220px]">
            <option value="">Default voice</option>
            {availableVoices.filter((voice) => String(voice.lang || '').toLowerCase().startsWith('en')).map((voice) => <option key={voice.voiceURI} value={voice.voiceURI}>{voice.name}</option>)}
          </select>
        </div>

        {/* Chat scroll area */}
        <div
          ref={scrollRef}
          className="px-5 py-4 h-[400px] overflow-y-auto bg-white space-y-3"
          data-testid="nexus-roleplay-scroll"
        >
          {messages.length === 0 && (
            <div className="text-sm text-gray-500 italic text-center py-8" data-testid="nexus-roleplay-empty">
              Start the roleplay — the AI is now in character as your client.<br/>
              Try opening with a reflection, an open question, or a reframe.
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === 'user' ? 'justify-end' : m.role === 'status' ? 'justify-center' : 'justify-start'}`}
              data-testid={`nexus-roleplay-msg-${i}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-snug ${
                  m.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-sm'
                    : m.role === 'status'
                      ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-lg'
                    : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                }`}
              >
                <div className={`text-[10px] uppercase tracking-wide mb-0.5 ${m.role === 'user' ? 'text-emerald-100' : m.role === 'status' ? 'text-amber-700' : 'text-gray-500'}`}>
                  {m.role === 'user' ? 'You (Therapist)' : m.role === 'status' ? 'Roleplay Status' : 'Client'}
                </div>
                {m.content}
                {m.role === 'status' && retrySeconds > 0 && (
                  <div className="mt-1 text-xs" data-testid="nexus-roleplay-retry-countdown">Try again in {retrySeconds} seconds.</div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="nexus-roleplay-typing">
              <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm text-gray-500 italic flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Client is thinking…
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t px-4 py-3 bg-gray-50 flex items-end gap-2">
          <button
            type="button"
            onClick={() => (isListening ? stopListening() : startListening())}
            disabled={sending || retrySeconds > 0}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-md border transition-colors flex-shrink-0 ${
              isListening
                ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100 animate-pulse'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="btn-roleplay-mic"
            title={
              !sttSupported
                ? 'Voice input not supported in this browser'
                : isListening
                  ? 'Stop listening'
                  : 'Hold to dictate your message'
            }
          >
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={isListening ? 'Listening… speak now' : 'Say something to your client…  (Enter to send · Shift+Enter for newline · click mic to dictate)'}
            rows={2}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            disabled={sending || retrySeconds > 0}
            data-testid="nexus-roleplay-input"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || retrySeconds > 0 || !input.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-2 inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            data-testid="btn-roleplay-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : retrySeconds > 0 ? `${retrySeconds}s` : <Send className="w-4 h-4" />}
          </button>
        </div>
        {sttError && <div className="px-4 pb-3 bg-gray-50 text-xs text-amber-700" role="status" data-testid="nexus-roleplay-stt-error">{sttError}</div>}
      </div>
    </div>
  );
}
